// DeployConfigStep — Generic deploy configuration for non-Railway targets.
// MANUS-WIZARD-V2 Task 3. For Fly.io, Render, and VPS/self-hosted.
import { useState } from 'react';
import type { DeployTarget } from '../../lib/path-matrix';

interface DeployConfigStepProps {
  deployTarget: DeployTarget;
  onComplete: (config: { deployUrl?: string; notes?: string }) => void;
  onSkip: () => void;
}

const PLATFORM_INFO: Record<string, { name: string; docsUrl: string; placeholder: string; description: string }> = {
  flyio: {
    name: 'Fly.io',
    docsUrl: 'https://fly.io/docs/getting-started/',
    placeholder: 'https://my-app.fly.dev',
    description: 'Fly.io deploys Docker containers globally. You\'ll need the Fly CLI installed and a fly.toml in your repo.',
  },
  render: {
    name: 'Render',
    docsUrl: 'https://docs.render.com/deploy-an-app',
    placeholder: 'https://my-app.onrender.com',
    description: 'Render auto-deploys from GitHub. Connect your repo in the Render dashboard and it will build on push.',
  },
  vps: {
    name: 'Self-hosted / VPS',
    docsUrl: '',
    placeholder: 'ssh://user@your-server.com or IP address',
    description: 'For self-hosted deployments, you\'ll manage your own server. OCNexus will create issues and PRs — you handle deployment.',
  },
};

export default function DeployConfigStep({ deployTarget, onComplete, onSkip }: DeployConfigStepProps) {
  const [deployUrl, setDeployUrl] = useState('');
  const [notes, setNotes] = useState('');

  const platform = PLATFORM_INFO[deployTarget] || PLATFORM_INFO.vps;

  return (
    <div style={{ padding: '24px 0' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
        Deploy Configuration — {platform.name}
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        {platform.description}
      </p>

      {platform.docsUrl && (
        <a
          href={platform.docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            marginBottom: 20,
            fontSize: 13,
            color: 'var(--blue)',
            textDecoration: 'underline',
          }}
        >
          View {platform.name} documentation →
        </a>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
          Deploy URL or target (optional)
        </label>
        <input
          type="text"
          value={deployUrl}
          onChange={e => setDeployUrl(e.target.value)}
          placeholder={platform.placeholder}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
            background: 'var(--surface)',
            color: 'var(--text)',
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Any additional deployment notes or environment variable targets..."
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
            background: 'var(--surface)',
            color: 'var(--text)',
            boxSizing: 'border-box',
            resize: 'vertical',
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={() => onComplete({ deployUrl: deployUrl || undefined, notes: notes || undefined })}
          style={{
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 600,
            border: 'none',
            borderRadius: 8,
            background: 'var(--blue)',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          Continue
        </button>
        <button
          onClick={onSkip}
          style={{
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 500,
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
